/**
 * Normalização de UF brasileira para o formato ISO 3166-2:BR exigido pelo PagBank
 * (`shipping.address.region_code`).
 *
 * Motivo: o banco pode conter valores como "São Paulo", "sao paulo" ou até
 * truncamentos inválidos ("SÃ", gerado por `slice(0, 2)` sobre o nome completo),
 * o que faz a API do PagBank retornar 40002
 * ("must be a valid region code by ISO 3166-2:BR").
 */

export const BRAZILIAN_UFS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS',
  'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC',
  'SP', 'SE', 'TO',
] as const;

export type BrazilianUF = typeof BRAZILIAN_UFS[number];

const STATE_NAME_TO_UF: Record<string, BrazilianUF> = {
  ACRE: 'AC',
  ALAGOAS: 'AL',
  AMAPA: 'AP',
  AMAZONAS: 'AM',
  BAHIA: 'BA',
  CEARA: 'CE',
  'DISTRITO FEDERAL': 'DF',
  'ESPIRITO SANTO': 'ES',
  GOIAS: 'GO',
  MARANHAO: 'MA',
  'MATO GROSSO': 'MT',
  'MATO GROSSO DO SUL': 'MS',
  'MINAS GERAIS': 'MG',
  PARA: 'PA',
  PARAIBA: 'PB',
  PARANA: 'PR',
  PERNAMBUCO: 'PE',
  PIAUI: 'PI',
  'RIO DE JANEIRO': 'RJ',
  'RIO GRANDE DO NORTE': 'RN',
  'RIO GRANDE DO SUL': 'RS',
  RONDONIA: 'RO',
  RORAIMA: 'RR',
  'SANTA CATARINA': 'SC',
  'SAO PAULO': 'SP',
  SERGIPE: 'SE',
  TOCANTINS: 'TO',
};

/** Remove acentos e normaliza espaços/caixa. */
const stripAccents = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();

/**
 * Converte qualquer representação de estado em UF válida.
 * @param raw valor bruto vindo do banco/formulário
 * @param fallback UF usada quando não é possível inferir (default: 'SP')
 */
export const normalizeRegionCode = (
  raw: string | null | undefined,
  fallback: BrazilianUF = 'SP',
): BrazilianUF => {
  if (!raw || typeof raw !== 'string') return fallback;

  const normalized = stripAccents(raw);
  if (!normalized) return fallback;

  // Já é uma UF válida
  if ((BRAZILIAN_UFS as readonly string[]).includes(normalized)) {
    return normalized as BrazilianUF;
  }

  // Nome completo do estado
  const byName = STATE_NAME_TO_UF[normalized];
  if (byName) return byName;

  // Prefixo de nome completo (ex.: truncamentos inválidos como "SA" de "São Paulo")
  const matches = Object.keys(STATE_NAME_TO_UF).filter((name) =>
    name.startsWith(normalized),
  );
  if (matches.length === 1) return STATE_NAME_TO_UF[matches[0]];

  return fallback;
};
